import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  User,
  Package,
  DollarSign,
  Receipt,
  RotateCcw,
  X,
  AlertCircle,
} from "lucide-react";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { useAuth } from "../../../context/Auth";
import { useGlobalContext } from "../../../context/stateContext";
import Loading from "../../../components/Loading/Loading";
import NotFound404 from "../../error/NotFound404";
import ReturnModal from "../../../components/modals/ReturnModal";

const SaleDetail = () => {
  const { requestWithAuth } = useAuth();
  const { branch_id, sale_id } = useParams();
  const { activeSession } = useGlobalContext();
  const navigate = useNavigate();

  const [sale, setSale] = useState(null);
  const [loading, setLoading] = useState(true);

  // Return Modal State
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [returnQuantity, setReturnQuantity] = useState("");
  const [returnReason, setReturnReason] = useState("");
  const [witnessId, setWitnessId] = useState("");

  const fetchSaleDetail = async () => {
    setLoading(true);
    try {
      const data = await requestWithAuth(`/inventory/sale/${sale_id}`, "GET");
      setSale(data);
    } catch (error) {
      console.error("Error fetching sale details:", error);
      toast.error("Failed to load sale details");
    } finally {
      setLoading(false);
    }
  };

  const openReturnModal = (item) => {
    setSelectedItem(item);
    setShowReturnModal(true);
  };

  const closeReturnModal = () => {
    setShowReturnModal(false);
    setSelectedItem(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      Active: {
        bg: "bg-green-100",
        text: "text-green-700",
        border: "border-green-200",
      },
      "Return Pending Confirmation": {
        bg: "bg-yellow-100",
        text: "text-yellow-700",
        border: "border-yellow-200",
      },
      Returned: {
        bg: "bg-gray-100",
        text: "text-gray-600",
        border: "border-gray-200",
      },
    };

    const config = statusConfig[status] || statusConfig["Active"];

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border ${config.bg} ${config.text} ${config.border}`}
      >
        {status || "Active"}
      </span>
    );
  };

  useEffect(() => {
    fetchSaleDetail();
  }, [sale_id]);

  if (loading) {
    return <Loading />;
  }

  if (!sale) {
    return <NotFound404 />;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(`/sales/list`)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm font-medium">Back to Sales</span>
          </button>

          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
                <span>Sales</span>
                <span>/</span>
                <span className="text-gray-900 font-medium">Sale Details</span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900">
                Invoice #{sale?.invoice_id || sale?.id}
              </h1>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Receipt className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Branch</p>
                <p className="text-lg font-bold text-gray-900">
                  {sale?.branch || "N/A"}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Cost</p>
                <p className="text-lg font-bold text-gray-900">
                  GHC {parseFloat(sale?.total_amount || 0).toFixed(2)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Calendar className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Date</p>
                <p className="text-sm font-semibold text-gray-900">
                  {formatDate(sale?.created_at || sale?.date)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-orange-100 rounded-lg">
                <User className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Sold By</p>
                <p className="text-sm font-semibold text-gray-900">
                  {sale?.recorded_by || "N/A"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Sale Items Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Package className="w-5 h-5 text-blue-500" />
              Sale Items
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              {sale?.items?.length || 0}{" "}
              {sale?.items?.length === 1 ? "item" : "items"} in this sale
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Drug Name
                  </th>
                  <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Batch
                  </th>
                  <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Quantity
                  </th>
                  <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Unit Cost
                  </th>
                  <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Total Cost
                  </th>
                  <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sale?.items?.map((item, index) => (
                  <tr
                    key={index}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="py-4 px-6 text-sm font-medium text-gray-900">
                      {item?.drug || "N/A"}
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-900">
                      {item?.batch || "N/A"}
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-600">
                      {item?.quantity}
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-900">
                      GHC {parseFloat(item?.unit_cost || 0).toFixed(2)}
                    </td>
                    <td className="py-4 px-6 text-sm font-semibold text-gray-900">
                      GHC{" "}
                      {parseFloat(item?.total_selling_price || 0).toFixed(2)}
                    </td>
                    <td className="py-4 px-6">
                      {getStatusBadge(item?.status)}
                    </td>
                    <td className="py-4 px-6">
                      {item?.status === "Returned" ||
                      item?.status === "Return Pending Confirmation" ? (
                        <span className="text-xs text-gray-500">
                          No action available
                        </span>
                      ) : (
                        <button
                          onClick={() => openReturnModal(item)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors border text-orange-700 bg-orange-50 hover:bg-orange-100 border-orange-200"
                        >
                          <RotateCcw size={14} />
                          Process Return
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                <tr>
                  <td
                    colSpan="4"
                    className="py-4 px-6 text-sm font-semibold text-gray-900"
                  >
                    Total
                  </td>
                  <td className="py-4 px-6 text-lg font-bold text-gray-900">
                    GHC {parseFloat(sale?.total_amount || 0).toFixed(2)}
                  </td>
                  <td colSpan="2"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      {/* Return Modal */}
      {showReturnModal && (
        <ReturnModal
          isOpen={showReturnModal}
          closeReturnModal={closeReturnModal}
          selectedItem={selectedItem}
          fetchSaleDetail={fetchSaleDetail}
        />
      )}
    </div>
  );
};

export default SaleDetail;
